import { tPaymentMethod } from '../../SubscriptionManager.types';

/**
 * M-21 (leadbilling) — the closed list of decline groups. Billing classifies
 * the Stripe code ONCE (`text_group`) and every surface reads the group, never
 * the raw code: the payment response, the summary's `last_decline` and, after
 * RET-2439, the Alert Center. One row per group in the approved copy table
 * (RET-2430). `authentication_incomplete` and `our_error` are not Stripe
 * declines; they share the notice because the customer reads them the same way.
 */
export type tPastDueDeclineGroup =
  | 'insufficient_funds'
  | 'processing_error'
  | 'temporary_bank_error'
  | 'expired_card'
  | 'incorrect_cvc'
  | 'security_block'
  | 'limit_reached'
  | 'brand_not_supported'
  | 'generic'
  | 'authentication_incomplete'
  | 'our_error';

/**
 * The decline the payment zone talks about: the renewal's (read from the
 * summary when the screen opens, `last_decline`) or the one that just happened
 * on this screen (the payment response). The most recent one wins (M-21).
 */
export type tPastDueDecline = {
  group: tPastDueDeclineGroup;
  /** M-21: only insufficient funds, a processing error, a temporary bank error or an unfinished authentication. */
  retryAllowed: boolean;
  /** The card the bank refused. `null` or a card no longer saved: the notice shows without «Reintentar con…». */
  paymentMethodId: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  /** For `brand_not_supported` (MO-6): «No podemos cobrar {{brand}} en {{currency}}». */
  currency: string | null;
  /** Decides «Rechazada hoy» against «Rechazada el {{date}}», in the customer's day. */
  declinedAt: Date | null;
  /** M-21: the refused card was just added here and both card slots went back: «Tu tarjeta de cobro no cambió.» */
  cardsRestored: 'restored' | 'not_restored' | 'not_applicable';
  /** `renewal`: Stripe's automatic charge, read when the screen opens. `payment`: a charge made on this screen or modal. */
  origin: 'renewal' | 'payment';
  /**
   * M-21 (Fanny, 23-oct-2026; numbers of 24-oct-2026): the retry cap of
   * this card. Billing counts the declines of the charges the customer asks
   * for, never Stripe's automatic retries, and turns `retryAllowed` off at 3
   * in a day (`daily`) or 5 in 30 days (`monthly`). With up to 8 automatic
   * retries, that stays under Visa's 15 in 30 days and Mastercard's 10 in 24
   * hours. The cap never stops the automatic retries. `null` while under
   * both caps.
   */
  retryLimit: 'daily' | 'monthly' | null;
};

/**
 * One line of an open invoice, as granular as Stripe gives it (Fanny,
 * 23-oct-2026). Billing sends the kind, never Stripe's own description: those
 * come in English («Remaining time on 4 × …»), so the front words each line.
 * Seats are ONE Stripe line with a graduated price (tier 1 = the seats the
 * plan includes, at $0); billing splits it into the included and the extra
 * seats with the price's tiers.
 */
export type tPastDueDebtLine = {
  kind:
    | 'plan'
    | 'seatsIncluded'
    | 'seatsExtra'
    | 'addOn'
    | 'pauseFee'
    | 'proration'
    /** Negative lines, so the lines add up to the total (M-16, 23-oct-2026). */
    | 'discount'
    | 'credit'
    | 'tax'
    /** The recurring wallet top-up, billed in the plan's invoice (RC-11). */
    | 'walletTopUp'
    | 'other';
  /** The add-on's name, for `addOn`. */
  name?: string;
  quantity: number | null;
  /** Formatted like `amountOwed`; `null` when the line has no unit price to show. */
  unitAmount: string | null;
  /** Formatted; negative for a credit. */
  amount: string;
  periodStart: Date | null;
  periodEnd: Date | null;
};

/**
 * One open invoice of the plan's subscription, for the breakdown of «Lo que
 * debes» (M-16, Fanny, 23-oct-2026). Billing sends the list with the same
 * function that sums the amount, so the invoices always add up to it.
 */
export type tPastDueDebtInvoice = {
  invoiceId: string;
  /** Stripe's invoice number: the reference for a bank transfer (M-20). */
  number: string;
  /** When Stripe finalized it: «Factura del 20 de octubre». */
  issuedAt: Date;
  lines: tPastDueDebtLine[];
  total: string;
  amountPaid: string;
  /** What is still owed on it: the invoices' remainders add up to `amountOwed`. */
  amountRemaining: string;
  /**
   * The invoice's PDF (`invoice_pdf`), NOT the hosted page: that page has its
   * own pay button, which charges one invoice outside the zone (Fanny,
   * 23-oct-2026; M-16).
   */
  invoicePdfUrl: string | null;
};

/**
 * Where a customer on manual collection transfers (M-20, Fanny, 23-oct-2026):
 * the customer's own Stripe bank account (`funding_instructions`, SPEI), the
 * same one on every invoice of theirs. Stripe applies a transfer to the open
 * invoices by itself when it arrives.
 */
export type tPastDueTransferInstructions = {
  bankName: string;
  /** 18 digits, unformatted. */
  clabe: string;
  accountHolderName: string;
};

/** What the zone is doing right now. `paid` leaves the zone: the plan is no longer past due. */
export type tPastDuePaymentPhase =
  | 'idle'
  /** The summary is still loading: no button, no error, nothing that reads as an amount. */
  | 'loading'
  | 'confirming'
  | 'partial'
  | 'amountUnavailable'
  /**
   * The customer chose «Pagar con un enlace de pago» and the page opened in
   * another tab: the zone waits, and refreshes on its own when the invoice is
   * paid (Fanny, 23-oct-2026).
   */
  | 'hostedOpened'
  /**
   * Paid, and Stripe already owes nothing, but the subscription status has not
   * caught up yet (the webhook is late): the zone says the payment is being
   * recorded, never «No pudimos leer cuánto debes» (M-16, 23-oct-2026).
   */
  | 'settling'
  /**
   * The debt changed while the customer was paying (someone else paid, a
   * Stripe retry charged it, another invoice was issued): nothing was
   * charged, and the zone shows the new amount (M-16, 23-oct-2026).
   */
  | 'debtChanged'
  /** Only the purchase modal reads it: the debt was just paid and the change goes on in the same step (M-18, M-19). */
  | 'paid'
  /**
   * Only the modal of a removal reads it: the debt was paid, but the removal
   * saved with it was not. The payment stays (the plan is no longer past due)
   * and only the removal is retried, without charging again (M-19, Fanny,
   * 23-oct-2026).
   */
  | 'paidChangeNotSaved';

/**
 * The past-due payment zone of Settings › Subscription (layout C, Fanny,
 * 21-oct-2026): what you owe, the cards to pay with — chosen right there, so
 * the decline notice stays in view while the customer picks another one — and
 * ONE primary button whose label follows the selected card.
 *
 * Provided by the subscription manager hook. The shape mirrors the shared
 * contract of the past-due payment (RET-2175 + RET-2430).
 */
export type tPastDuePaymentView = {
  /** Formatted with `formatAmountOwed`; `null` → `amountUnavailable`. */
  amountOwed: string | null;
  /** What `amountOwed` is made of, oldest first; old invoices included (M-16, M-17). */
  debtInvoices: tPastDueDebtInvoice[];
  nextRetryDate: Date | null;
  paymentMethods: tPaymentMethod[];
  selectedPaymentMethodId: string | null;
  /**
   * The card Stripe charges the plan with (MO-5). Paying with another one makes
   * it the plan's card (M-21, MO-7), and the legend says so before the click.
   * `null` on manual collection (M-20): there is no plan card there.
   */
  planPaymentMethodId: string | null;
  /**
   * The Alert Center's past-due strip is on screen with its deadline (last
   * rung): the block on the left does not repeat the date (Fanny, 21-oct).
   */
  deadlineShownAbove: boolean;
  /** The debt is the fee of a plan pause (PP-*): «Es la cuota de tu pausa.» under the amount. */
  isPauseFee: boolean;
  /**
   * The plan is paid by invoice, not charged to a card (M-20, MO-5): there is no
   * automatic retry, and the legal text under the button says the payment only
   * covers the debt (Fanny, 23-oct-2026).
   */
  isManualCollection: boolean;
  /**
   * Manual collection only: when the oldest open invoice is due. In the future
   * the zone already shows, before the past due starts (M-20); in the past,
   * it replaces the retry line, since nothing retries on manual collection.
   */
  dueDate: Date | null;
  /** Manual collection only; `null` when Stripe's instructions could not be read. */
  transferInstructions: tPastDueTransferInstructions | null;
  /**
   * A transfer for less than the invoice: Stripe does not pay part of it, the
   * money waits as the customer's cash balance. The zone says what arrived
   * and asks only for what is missing (M-20, Fanny, 24-oct-2026).
   */
  transferReceived: { received: string; missing: string } | null;
  /**
   * Stripe gives bank-transfer data only in MXN. Manual collection in another
   * currency shows the card as the main path, plus the payment link (M-20,
   * Fanny, 24-oct-2026). No such subscription existed on 24-oct-2026.
   */
  transferSupported: boolean;
  /**
   * Stripe's hosted page of the ONE open invoice, offered in the card list as
   * «Pagar con un enlace de pago» (Fanny, 23-oct-2026). `null` with two or
   * more open invoices: that page would charge only one and leave the
   * customer past due (M-16).
   */
  hostedPaymentUrl: string | null;
  phase: tPastDuePaymentPhase;
  decline: tPastDueDecline | null;
  partial: { paid: string; total: string; remaining: string } | null;
  onSelectPaymentMethod: (paymentMethodId: string) => void;
  onAddPaymentMethod: () => void;
  onPay: () => void;
};

/**
 * M-21 (Fanny, 21-oct-2026): the first click does not charge the refused card
 * again. On entry, another saved card without a decline comes selected when
 * there is one; the refused card keeps its mark and notice and can still be
 * chosen to retry. With no other card, the refused one comes selected.
 */
export function isExpiredCard(card: {
  year?: number | null;
  month?: number | null;
}): boolean {
  if (!card.year || !card.month) return false;
  const now = new Date();
  // A card is good through the last day of its expiry month.
  return (
    card.year < now.getFullYear() ||
    (card.year === now.getFullYear() && card.month < now.getMonth() + 1)
  );
}

export function resolveInitialPastDueSelection(args: {
  paymentMethods: {
    paymentMethodId: string;
    year?: number | null;
    month?: number | null;
  }[];
  decline: tPastDueDecline | null;
  preferredPaymentMethodId: string | null;
}): string | null {
  const refused =
    args.decline && args.decline.group !== 'our_error'
      ? args.decline.paymentMethodId
      : null;
  const preferred = args.paymentMethods.find(
    (pm) => pm.paymentMethodId === args.preferredPaymentMethodId,
  );
  // An expired card is a sure decline that also counts toward the retry cap:
  // it is marked «Vencida» and can never be selected (M-21, Fanny, 24-oct-2026).
  const usable = args.paymentMethods.filter((pm) => !isExpiredCard(pm));
  if (
    preferred &&
    preferred.paymentMethodId !== refused &&
    !isExpiredCard(preferred)
  ) {
    return preferred.paymentMethodId;
  }
  const other = usable.find((pm) => pm.paymentMethodId !== refused);
  return other?.paymentMethodId ?? usable[0]?.paymentMethodId ?? null;
}

/** For callers that must run the pay-button hook before knowing whether there is a past-due view. */
export const PAST_DUE_PAYMENT_EMPTY_VIEW: tPastDuePaymentView = {
  amountOwed: null,
  debtInvoices: [],
  nextRetryDate: null,
  paymentMethods: [],
  selectedPaymentMethodId: null,
  planPaymentMethodId: null,
  deadlineShownAbove: false,
  isPauseFee: false,
  isManualCollection: false,
  dueDate: null,
  transferInstructions: null,
  transferReceived: null,
  transferSupported: false,
  hostedPaymentUrl: null,
  phase: 'idle',
  decline: null,
  partial: null,
  onSelectPaymentMethod: () => undefined,
  onAddPaymentMethod: () => undefined,
  onPay: () => undefined,
};

/**
 * The selection value of the «Pagar con un enlace de pago» option. It lives
 * in the same radio group as the saved cards, so it is never preselected
 * (`resolveInitialPastDueSelection` only looks at saved cards).
 */
export const PAST_DUE_HOSTED_OPTION = 'stripe-hosted-page';
